import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Send, Sparkles, Wind, Volume2, Square, Contrast, Mic, Loader2, Radio, Lock, ImagePlus, Headphones, Palette, History, Compass, HelpCircle, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MemberAvatar } from "@/components/MemberAvatar";
import UpsellModal from "@/components/UpsellModal";

const MENTOR_MARKUP = /(\*\*[^*]+\*\*|==[^=]+==|\[\[[^\]]+\]\])/g;
const stripMarkup = (s = "") =>
  s.replace(/\*\*([^*]+)\*\*/g, "$1").replace(/==([^=]+)==/g, "$1").replace(/\[\[([^\]]+)\]\]/g, "$1");

function renderNumbers(text, kp) {
  const NUM = /\d[\d\s.,]*\d(?:\s?(?:%|°C|°|km\/h|km|kg|ans|années?|millions?|milliards?|siècles?|€|\$))?|\b\d(?:\s?(?:%|°C|°|km\/h|km|kg|ans|années?|millions?|milliards?|siècles?|€|\$))?/g;
  const out = [];
  let last = 0, m, idx = 0;
  while ((m = NUM.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<span key={`${kp}-n${idx++}`} className="spark-num">{m[0].trim()}</span>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function renderInline(text, onWord, kp) {
  return text.split(MENTOR_MARKUP).map((p, i) => {
    const key = `${kp}-${i}`;
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={key} className="text-primary font-semibold not-italic">{p.slice(2, -2)}</strong>;
    if (p.startsWith("==") && p.endsWith("==")) return <mark key={key} className="spark-pepite">{p.slice(2, -2)}</mark>;
    if (p.startsWith("[[") && p.endsWith("]]")) {
      const w = p.slice(2, -2);
      return <button key={key} type="button" onClick={() => onWord && onWord(w)} className="spark-word" data-testid="fil-word">{w}</button>;
    }
    return <span key={key}>{renderNumbers(p, key)}</span>;
  });
}

function MentorText({ content, onWord }) {
  const blocks = (content || "").split(/\n+/).map((s) => s.trim()).filter(Boolean);
  return (
    <div className="space-y-3" data-testid="mentor-text">
      {blocks.map((line, bi) => (
        <p key={bi} className="font-display italic text-foreground/90 leading-[1.85] text-xl">
          {renderInline(line, onWord, bi)}
        </p>
      ))}
    </div>
  );
}

function SocraticGauge({ socratic, t }) {
  if (!socratic) return null;
  const phase = socratic.phase || "verifie";
  const level = Math.max(0, Math.min(100, socratic.level ?? 0));
  const done = phase === "valide";
  const labels = {
    amorce: t("atelier.socraticPhaseAmorce"),
    verifie: t("atelier.socraticPhaseVerifie"),
    relance: t("atelier.socraticPhaseRelance"),
    valide: t("atelier.socraticPhaseValide"),
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="socratic-gauge"
      className={`mt-4 rounded-2xl border px-4 py-3 ${done ? "border-emerald-300/35 bg-emerald-300/[0.06]" : "border-primary/25 bg-primary/[0.05]"}`}
    >
      <div className="flex items-center gap-2 mb-2">
        {done ? <Check size={15} className="text-emerald-300" /> : <HelpCircle size={15} className="text-primary" />}
        <span className={`text-xs uppercase tracking-[0.18em] ${done ? "text-emerald-300/90" : "text-primary/90"}`} data-testid="socratic-phase">
          {labels[phase] || labels.verifie}
        </span>
        <span className="ml-auto text-[0.7rem] tabular-nums text-foreground/45" data-testid="socratic-level">{t("atelier.socraticLevel")} · {level}%</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden bg-background/40 border border-white/10">
        <motion.div
          className={`h-full ${done ? "bg-emerald-300/80" : "bg-gradient-to-r from-primary/70 to-primary"}`}
          initial={{ width: 0 }}
          animate={{ width: `${level}%` }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />
      </div>
    </motion.div>
  );
}

export default function FilDeCuriosite({ pendingPrompt }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const unlimited = !!(user && user.unlimited);
  const lang = (i18n.language || "fr").slice(0, 2);
  const API = process.env.REACT_APP_BACKEND_URL;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [derive, setDerive] = useState(null);
  const [deriveOpen, setDeriveOpen] = useState(true);
  const [souvenir, setSouvenir] = useState(null);
  const [souvenirOpen, setSouvenirOpen] = useState(true);
  const [paths, setPaths] = useState([]);
  const [speakingId, setSpeakingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [miroirLoading, setMiroirLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [autoPlay, setAutoPlay] = useState(() => localStorage.getItem("coccon_autoplay") === "1");
  const [remaining, setRemaining] = useState(null);
  const [limit, setLimit] = useState(5);
  const [mode, setMode] = useState(null);
  const [level, setLevel] = useState("adulte");
  const [importing, setImporting] = useState(false);
  const [podcasting, setPodcasting] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [upsellReason, setUpsellReason] = useState(null);
  const fileRef = useRef(null);

  const makePodcast = async () => {
    if (!unlimited) {
      upsell(t("atelier.voicePaidTts"));
      return;
    }
    if (podcasting || sending) return;
    setPodcasting(true);
    try {
      const { data } = await api.post("/fil/podcast", { lang });
      const aid = `pod-${Date.now()}`;
      setMessages((m) => [...m, { id: aid, role: "assistant", content: data.script }]);
      toast.success(t("atelier.podcastAdded"), { description: data.title });
      speak(aid, data.script);
    } catch (e) {
      if (e.response?.status === 402) upsell(t("atelier.voicePaidTts"));
      else if (e.response?.status === 400) toast(t("atelier.podcastEmpty"));
      else toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.silent"));
    } finally {
      setPodcasting(false);
    }
  };

  const importImage = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error(t("atelier.importFormat"));
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      toast.error(t("atelier.importTooBig"));
      return;
    }
    if (!unlimited && remaining === 0) {
      upsell(t("atelier.limitReached"));
      return;
    }
    const promptText = input.trim();
    setInput("");
    setImporting(true);
    activeExchangeRef.current = true;
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: `[Image] ${promptText || t("atelier.importDefault")}` }]);
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("prompt", promptText);
      fd.append("lang", lang);
      const { data } = await api.post("/fil/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const aid = data.message_id || `a-${Date.now()}`;
      setMessages((m) => [...m, { id: aid, role: "assistant", content: data.reply }]);
      setPaths(data.paths || []);
      if (!unlimited) setRemaining((r) => (r == null ? r : Math.max(0, r - 1)));
      if (autoPlay) speak(aid, data.reply);
      if (data.new_stars?.length) toast(t("atelier.starsToast", { count: data.new_stars.length }), { description: data.new_stars.join(" · ") });
      if (data.epiphany) toast.success(t("atelier.epiphanyToast"), { description: data.epiphany.title });
      if (unlimited && data.message_id) illustrate(data.message_id);
    } catch (err) {
      if (err.response?.status === 402) {
        upsell(t("atelier.limitReached"));
        setRemaining(0);
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("atelier.silent"));
      }
      setMessages((m) => m.filter((x) => !String(x.id).startsWith("tmp-")));
    } finally {
      setImporting(false);
    }
  };
  const bottomRef = useRef(null);
  const lastUserRef = useRef(null);
  const didScrollInit = useRef(false);
  const activeExchangeRef = useRef(false);
  const handledRef = useRef(null);
  const didLoadRef = useRef(false);
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const load = async () => {
    try {
      const { data } = await api.get("/fil");
      setMessages(data.map((m) => ({ ...m, imageId: m.image_id || null, caption: m.caption || null })));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const illustrate = async (messageId, manual = false) => {
    if (!messageId) return;
    if (!unlimited) {
      if (manual) upsell(t("atelier.illustratePaid"));
      return;
    }
    setMessages((m) => m.map((x) => (x.id === messageId ? { ...x, illustrating: true } : x)));
    try {
      const { data } = await api.post("/fil/illustrate", { message_id: messageId, lang });
      setMessages((m) =>
        m.map((x) =>
          x.id === messageId
            ? { ...x, illustrating: false, imageId: data.image_id || null, caption: data.caption || null, illustrated: true }
            : x
        )
      );
      if (manual && !data.image_id) toast(t("atelier.illustrateSkip"));
    } catch (e) {
      setMessages((m) => m.map((x) => (x.id === messageId ? { ...x, illustrating: false } : x)));
      if (e.response?.status === 402) upsell(t("atelier.illustratePaid"));
      else if (manual) toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.illustrateFail"));
    }
  };

  useEffect(() => {
    if (didLoadRef.current) return;
    didLoadRef.current = true;
    load();
    api.get("/derive").then(({ data }) => setDerive(data.prompt)).catch(() => {});
    api.get(`/fil/souvenir?lang=${lang}`).then(({ data }) => setSouvenir(data.souvenir)).catch(() => {});
    api.get("/usage").then(({ data }) => { setRemaining(data.remaining); if (data.limit) setLimit(data.limit); }).catch(() => {});
  }, []);

  const upsell = (msg) => {
    setUpsellReason(msg || t("atelier.limitReached"));
    setUpsellOpen(true);
  };

  const followPath = (p) => {
    setPaths([]);
    send(p);
  };

  const toggleSocratic = () => {
    const next = mode === "socratic" ? null : "socratic";
    setMode(next);
    toast(next ? t("atelier.socraticOn") : t("atelier.socraticOff"));
  };

  const resumeSouvenir = () => {
    if (!souvenir) return;
    setSouvenirOpen(false);
    send(t("atelier.souvenirPrompt", { topic: souvenir.topic }));
  };

  useEffect(() => {
    if (loading) return;
    const last = messages[messages.length - 1];
    if (!last) return;
    if (!didScrollInit.current) {
      // Au premier chargement : on ancre la dernière question en haut si elle existe,
      // sinon on descend au dernier message.
      didScrollInit.current = true;
      if (lastUserRef.current) {
        lastUserRef.current.scrollIntoView({ behavior: "auto", block: "start" });
      } else {
        bottomRef.current?.scrollIntoView({ behavior: "auto" });
      }
      return;
    }
    // Pendant un échange actif, on garde la question de l'utilisateur en haut de la zone
    // de lecture — à l'envoi ET quand la réponse arrive (c'est là que l'espace se libère
    // grâce à l'espaceur en bas), pour lire la réponse depuis son début.
    if (activeExchangeRef.current) {
      lastUserRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (last.role === "assistant" && !sending) activeExchangeRef.current = false;
    }
  }, [messages, sending, loading]);

  useEffect(() => {
    if (pendingPrompt && pendingPrompt.ts !== handledRef.current && !loading) {
      handledRef.current = pendingPrompt.ts;
      send(pendingPrompt.text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPrompt, loading]);

  const digWord = (word) => {
    const p = { fr: `Parle-moi de « ${word} »`, en: `Tell me about “${word}”`, es: `Háblame de «${word}»`, nl: `Vertel me over “${word}”` };
    send(p[lang] || p.fr);
  };

  const send = async (override) => {
    const text = (typeof override === "string" ? override : input).trim();
    if (!text || sending) return;
    if (!unlimited && remaining === 0) {
      upsell(t("atelier.limitReached"));
      return;
    }
    if (typeof override !== "string") setInput("");
    setPaths([]);
    activeExchangeRef.current = true;
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/fil", {
        message: text,
        lang,
        mode: mode || undefined,
        level: mode === "level" ? level : undefined,
      });
      const aid = data.message_id || `a-${Date.now()}`;
      setMessages((m) => [...m, { id: aid, role: "assistant", content: data.reply, socratic: data.socratic || null }]);
      setPaths(data.paths || []);
      if (!unlimited) setRemaining((r) => (r == null ? r : Math.max(0, r - 1)));
      if (autoPlay) speak(aid, data.reply);
      if (data.new_stars?.length) {
        toast(t("atelier.starsToast", { count: data.new_stars.length }), {
          description: data.new_stars.join(" · "),
        });
      }
      if (data.epiphany) {
        toast.success(t("atelier.epiphanyToast"), { description: data.epiphany.title });
      }
      if (unlimited && data.message_id) illustrate(data.message_id);
    } catch (e) {
      if (e.response?.status === 402) {
        upsell(t("atelier.limitReached"));
        setRemaining(0);
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.silent"));
      }
      setMessages((m) => m.filter((x) => !String(x.id).startsWith("tmp-")));
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const copyMessage = (id, text) => {
    const clean = stripMarkup(text || "");
    const done = () => {
      setCopiedId(id);
      toast.success(t("atelier.copied"));
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1800);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(clean).then(done).catch(() => {
        try {
          const ta = document.createElement("textarea");
          ta.value = clean; ta.style.position = "fixed"; ta.style.opacity = "0";
          document.body.appendChild(ta); ta.focus(); ta.select();
          document.execCommand("copy"); document.body.removeChild(ta); done();
        } catch { toast.error(t("atelier.copyError")); }
      });
    } else {
      try {
        const ta = document.createElement("textarea");
        ta.value = clean; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta); done();
      } catch { toast.error(t("atelier.copyError")); }
    }
  };

  const speak = async (id, text) => {
    if (!unlimited) {
      upsell(t("atelier.voicePaidTts"));
      return;
    }
    if (speakingId === id) {
      audioRef.current?.pause();
      setSpeakingId(null);
      return;
    }
    audioRef.current?.pause();
    setSpeakingId(id);
    try {
      const { data } = await api.post("/speak", { message: stripMarkup(text) });
      const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
      audioRef.current = audio;
      audio.onended = () => setSpeakingId(null);
      await audio.play();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.voiceHoarse"));
      setSpeakingId(null);
    }
  };

  const askMiroir = async () => {
    if (miroirLoading || sending) return;
    setMiroirLoading(true);
    try {
      const { data } = await api.post(`/miroir?lang=${lang}`);
      setMessages((m) => [...m, { id: `mir-${Date.now()}`, role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.mirrorFoggy"));
    } finally {
      setMiroirLoading(false);
    }
  };

  const toggleAutoPlay = () => {
    if (!unlimited) {
      upsell(t("atelier.voicePaidTts"));
      return;
    }
    setAutoPlay((v) => {
      const next = !v;
      localStorage.setItem("coccon_autoplay", next ? "1" : "0");
      if (!next) {
        audioRef.current?.pause();
        setSpeakingId(null);
      }
      return next;
    });
  };

  const startRecording = async () => {
    if (!unlimited) {
      upsell(t("atelier.voicePaidStt"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const form = new FormData();
          form.append("audio", blob, "voix.webm");
          const { data } = await api.post(`/transcribe?lang=${lang}`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const txt = (data.text || "").trim();
          if (txt) setInput((prev) => (prev ? prev + " " : "") + txt);
          else toast(t("atelier.heardNothing"));
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.transcribeFail"));
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error(t("atelier.micDenied"));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const lastUserId = messages.reduce((acc, mm) => (mm.role === "user" ? mm.id : acc), null);

  return (
    <div className="h-full flex flex-col">
      <UpsellModal open={upsellOpen} reason={upsellReason} onClose={() => setUpsellOpen(false)} />
      <div className="flex-1 overflow-y-auto scroll-thin">
        <div className="max-w-3xl mx-auto px-6 py-10 space-y-10">
          <AnimatePresence>
            {souvenir && souvenirOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                data-testid="souvenir-card"
                className="glass rounded-2xl p-6 relative overflow-hidden border border-primary/10"
              >
                <div className="flex items-center gap-2 mb-3">
                  <History size={16} className="text-primary" />
                  <span className="uppercase tracking-[0.22em] text-xs text-primary/90">{t("atelier.souvenirEyebrow")}</span>
                </div>
                <p className="font-display italic text-xl text-foreground/85 leading-relaxed">{souvenir.text}</p>
                <div className="flex items-center gap-4 mt-5">
                  <button
                    data-testid="souvenir-resume-btn"
                    onClick={resumeSouvenir}
                    className="text-sm font-medium px-5 py-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    {t("atelier.souvenirResume")}
                  </button>
                  <button
                    data-testid="souvenir-dismiss-btn"
                    onClick={() => setSouvenirOpen(false)}
                    className="text-sm text-foreground/40 hover:text-foreground/70 transition-colors"
                  >
                    {t("atelier.souvenirDismiss")}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {derive && deriveOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                data-testid="derive-card"
                className="glass rounded-2xl p-6 relative overflow-hidden"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Wind size={16} className="text-primary" />
                  <span className="uppercase tracking-[0.22em] text-xs text-primary/90">{lang === "en" ? "The daily drift" : lang === "es" ? "La deriva del día" : lang === "nl" ? "De dagelijkse drift" : "La dérive du jour"}</span>
                </div>
                <p className="font-display italic text-xl text-foreground/85 leading-relaxed">{derive}</p>
                <div className="flex items-center gap-4 mt-5">
                  <button
                    data-testid="derive-explore-btn"
                    onClick={() => {
                      setDeriveOpen(false);
                      send(derive);
                    }}
                    className="text-sm font-medium px-5 py-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    {lang === "en" ? "Follow this thread" : lang === "es" ? "Seguir este hilo" : lang === "nl" ? "Volg deze draad" : "Suivre ce fil"}
                  </button>
                  <button
                    data-testid="derive-dismiss-btn"
                    onClick={() => setDeriveOpen(false)}
                    className="text-sm text-foreground/40 hover:text-foreground/70 transition-colors"
                  >
                    {lang === "en" ? "Another time" : lang === "es" ? "En otro momento" : lang === "nl" ? "Een andere keer" : "Une autre fois"}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          {loading ? (
            <p className="font-display italic text-foreground/40 text-xl">{t("atelier.reloading")}</p>
          ) : (
            messages.map((m) => (
              <motion.div
                key={m.id}
                ref={m.id === lastUserId ? lastUserRef : undefined}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                data-testid={`msg-${m.role}`}
                className={m.id === lastUserId ? "scroll-mt-6" : undefined}
              >
                {m.role === "assistant" ? (
                  <div className="flex gap-4 pl-0 md:pl-2">
                    <Sparkles className="text-primary mt-1.5 flex-shrink-0 pulse-star" size={20} />
                    <div className="border-l border-primary/20 pl-5 flex-1">
                      <MentorText content={m.content} onWord={digWord} />
                      {m.socratic && <SocraticGauge socratic={m.socratic} t={t} />}
                      {m.illustrating && (
                        <div className="mt-4 flex items-center gap-2 text-xs text-foreground/40" data-testid={`illustrating-${m.id}`}>
                          <Loader2 size={14} className="animate-spin text-primary" /> {t("atelier.illustrating")}
                        </div>
                      )}
                      {m.imageId && (
                        <figure className="mt-4" data-testid={`illustration-${m.id}`}>
                          <div className="overflow-hidden rounded-2xl border border-primary/20 max-w-md">
                            <img
                              src={`${API}/api/fil/illustration/${m.imageId}`}
                              alt={m.caption || ""}
                              loading="lazy"
                              className="w-full h-auto block"
                              data-testid={`illustration-img-${m.id}`}
                            />
                          </div>
                          {m.caption && (
                            <figcaption className="mt-2 text-xs italic text-foreground/45 flex items-center gap-1.5">
                              <Palette size={12} className="text-primary/70" /> {m.caption}
                            </figcaption>
                          )}
                        </figure>
                      )}
                      <div className="mt-3 flex items-center gap-4">
                        <button
                          data-testid={`speak-btn-${m.id}`}
                          onClick={() => speak(m.id, m.content)}
                          className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-foreground/40 hover:text-primary transition-colors"
                        >
                          {!unlimited ? <Lock size={12} /> : speakingId === m.id ? <Square size={13} /> : <Volume2 size={14} />}
                          {speakingId === m.id ? t("atelier.silence") : t("atelier.ecouter")}
                        </button>
                        {!m.imageId && !m.illustrating && (
                          <button
                            data-testid={`illustrate-btn-${m.id}`}
                            onClick={() => illustrate(m.id, true)}
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-foreground/40 hover:text-primary transition-colors"
                          >
                            {unlimited ? <Palette size={13} /> : <Lock size={12} />}
                            {t("atelier.illustrate")}
                          </button>
                        )}
                        <button
                          data-testid={`copy-btn-${m.id}`}
                          onClick={() => copyMessage(m.id, m.content)}
                          className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-foreground/40 hover:text-primary transition-colors"
                        >
                          {copiedId === m.id ? <Check size={14} className="text-primary" /> : <Copy size={13} />}
                          {copiedId === m.id ? t("atelier.copied") : t("atelier.copy")}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-end items-start gap-2.5">
                    <p className="font-display italic text-2xl text-foreground/70 max-w-xl text-right leading-relaxed">
                      « {m.content} »
                    </p>
                    <MemberAvatar uid={user?.id} avatar={!!user?.avatar_photo} name={user?.name} cocon={user?.role === "admin" || user?.plan === "cocon"} size={30} />
                  </div>
                )}
              </motion.div>
            ))
          )}
          {sending && (
            <div className="flex gap-4 pl-0 md:pl-2">
              <Sparkles className="text-primary mt-1.5 pulse-star" size={20} />
              <p className="font-display italic text-foreground/40 text-lg">{t("atelier.thinking")}</p>
            </div>
          )}
          {paths.length > 0 && !sending && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              data-testid="chemins-traverse"
              className="pl-0 md:pl-9"
            >
              <div className="flex items-center gap-2 mb-3 text-primary/70">
                <Compass size={14} />
                <span className="uppercase tracking-[0.2em] text-xs">{t("atelier.pathsLabel")}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {paths.map((p, i) => (
                  <button
                    key={i}
                    data-testid={`chemin-${i}`}
                    onClick={() => followPath(p)}
                    className="text-sm px-4 py-2 rounded-full border border-primary/25 text-foreground/75 hover:bg-primary/10 hover:text-primary hover:border-primary/50 transition-colors text-left"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
          <div aria-hidden style={{ minHeight: "60vh" }} />
          <div ref={bottomRef} />
        </div>
      </div>

      <div className="flex-shrink-0 border-t border-white/10 glass">
        <div className="w-full px-3 sm:px-6 py-3">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <button
              data-testid="miroir-btn"
              onClick={askMiroir}
              disabled={miroirLoading || sending}
              title={t("atelier.miroir")}
              aria-label={t("atelier.miroir")}
              className="w-9 h-9 rounded-full border border-primary/30 text-primary flex items-center justify-center hover:bg-primary/10 transition-colors disabled:opacity-40 flex-shrink-0"
            >
              {miroirLoading ? <Loader2 size={15} className="animate-spin" /> : <Contrast size={16} />}
            </button>
            <button
              data-testid="socratic-btn"
              onClick={toggleSocratic}
              title={t("atelier.socraticLabel")}
              aria-label={t("atelier.socraticLabel")}
              className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors flex-shrink-0 ${
                mode === "socratic"
                  ? "border-primary bg-primary/15 text-primary"
                  : "border-white/15 text-foreground/50 hover:text-foreground/80"
              }`}
            >
              <HelpCircle size={16} />
            </button>
            <button
              data-testid="autoplay-btn"
              onClick={toggleAutoPlay}
              title={`${t("atelier.voixAuto")} · ${autoPlay ? t("atelier.on") : t("atelier.off")}`}
              aria-label={t("atelier.voixAuto")}
              className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors flex-shrink-0 ${
                autoPlay
                  ? "border-primary bg-primary/15 text-primary"
                  : "border-white/15 text-foreground/50 hover:text-foreground/80"
              }`}
            >
              {unlimited ? <Radio size={16} /> : <Lock size={13} />}
            </button>
            <button
              data-testid="podcast-btn"
              onClick={makePodcast}
              disabled={podcasting || sending}
              title={t("atelier.toolPodcast")}
              aria-label={t("atelier.toolPodcast")}
              className="w-9 h-9 rounded-full border border-primary/30 text-primary flex items-center justify-center hover:bg-primary/10 transition-colors disabled:opacity-40 flex-shrink-0"
            >
              {podcasting ? <Loader2 size={15} className="animate-spin" /> : unlimited ? <Headphones size={16} /> : <Lock size={13} />}
            </button>
            {!unlimited && remaining !== null && (
              <div className="ml-auto flex items-center gap-2 flex-shrink-0">
                {remaining <= 1 && (
                  <button
                    data-testid="discover-elan-btn"
                    onClick={() => navigate("/tarifs")}
                    className="text-xs px-3 py-1.5 rounded-full bg-primary/15 text-primary hover:bg-primary/25 transition-colors inline-flex items-center gap-1.5 whitespace-nowrap"
                  >
                    <Sparkles size={12} /> {t("atelier.discoverElan")}
                  </button>
                )}
                <button
                  data-testid="messages-left"
                  onClick={() => navigate("/tarifs")}
                  className={`text-xs px-3 py-1.5 rounded-full transition-colors whitespace-nowrap ${
                    remaining > 0
                      ? "text-foreground/50 hover:text-primary"
                      : "bg-primary/15 text-primary hover:bg-primary/25"
                  }`}
                >
                  {remaining > 0
                    ? t("atelier.exchangesToday", { used: Math.max(0, limit - remaining), limit })
                    : t("atelier.limitBanner")}
                </button>
              </div>
            )}
          </div>
          <div className="flex items-end gap-2 sm:gap-3">
            <textarea
              data-testid="fil-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={!unlimited && remaining === 0}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={3}
              placeholder={
                !unlimited && remaining === 0
                  ? t("atelier.limitInput")
                  : recording
                  ? t("atelier.listening")
                  : t("atelier.placeholder")
              }
              className="flex-1 bg-transparent resize-none outline-none text-foreground placeholder:text-foreground/30 text-lg leading-relaxed py-2 min-h-[5.5rem] max-h-60 scroll-thin disabled:cursor-not-allowed disabled:placeholder:text-primary/60 disabled:placeholder:italic"
            />
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={importImage} className="hidden" data-testid="import-input" />
            <button
              data-testid="import-btn"
              onClick={() => fileRef.current?.click()}
              disabled={importing || sending}
              title={t("atelier.importTitle")}
              className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center border border-primary/40 text-primary hover:bg-primary/10 transition-colors disabled:opacity-40"
            >
              {importing ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
            </button>
            <button
              data-testid="mic-btn"
              onClick={recording ? stopRecording : startRecording}
              disabled={transcribing || sending}
              title="Parler à L'Élan"
              className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 ${
                recording
                  ? "bg-red-500/80 text-white animate-pulse"
                  : "border border-primary/40 text-primary hover:bg-primary/10"
              }`}
            >
              {transcribing ? <Loader2 size={18} className="animate-spin" /> : !unlimited ? <Lock size={16} /> : recording ? <Square size={16} /> : <Mic size={18} />}
            </button>
            <button
              data-testid="fil-send-btn"
              onClick={() => send()}
              disabled={sending || !input.trim()}
              title={t("atelier.send", "Envoyer")}
              className="flex-shrink-0 w-11 h-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-[0_0_18px_-4px_hsl(38_95%_55%/0.7)] hover:bg-primary/90 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
